import React, { useEffect, useState, useRef } from "react";
import { NavLink } from "react-router-dom";
import { Translate } from "../../helpers/Translate";
import axios from "axios";
import { BASE_URL } from "../../variables/api";
import { annotate } from "rough-notation";

function DashBoardNav(props: any) {
  let t = new Translate();
  const [userStats, setUserStats] = useState<any>(null);
  const [showStats, setShowStats] = useState(false);
  const scoreRef = useRef<HTMLSpanElement>(null);
  const subjectRef = useRef<HTMLSpanElement>(null);
  const pendingRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef<HTMLDivElement>(null);
  const gradedRef = useRef<HTMLSpanElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Fetch user stats
    axios
      .get(`${BASE_URL}student/getUserInfoAndStats.php`)
      .then((response) => {
        if (response.data.status === "success") {
          setUserStats(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching user stats:", error);
      });
  }, []);

  useEffect(() => {
    if (showStats && userStats) {
      // Annotate the score with highlight
      if (scoreRef.current) {
        const scoreAnnotation = annotate(scoreRef.current, {
          type: "highlight",
          color:
            userStats.average_percentage >= 90
              ? "#28a745"
              : userStats.average_percentage >= 75
                ? "#ffc107"
                : "#ff6b6b",
          iterations: 1,
          animationDuration: 800,
        });
        scoreAnnotation.show();
      }

      // Annotate the subject with underline
      if (subjectRef.current) {
        setTimeout(() => {
          if (subjectRef.current) {
            const subjectAnnotation = annotate(subjectRef.current, {
              type: "underline",
              color: "#007bff",
              iterations: 2,
              animationDuration: 800,
            });
            subjectAnnotation.show();
          }
        }, 400);
      }

      // Annotate pending count with highlight
      if (pendingRef.current && userStats.pending_exams_count > 0) {
        setTimeout(() => {
          if (pendingRef.current) {
            const pendingAnnotation = annotate(pendingRef.current, {
              type: "highlight",
              color: "#ffc107",
              iterations: 1,
              animationDuration: 800,
            });
            pendingAnnotation.show();
          }
        }, 800);
      }

      // Annotate started count with highlight
      if (startedRef.current && userStats.started_exams_count > 0) {
        setTimeout(() => {
          if (startedRef.current) {
            const startedAnnotation = annotate(startedRef.current, {
              type: "highlight",
              color: "#17a2b8",
              iterations: 1,
              animationDuration: 800,
            });
            startedAnnotation.show();
          }
        }, 1000);
      }

      // Annotate graded count with box
      if (gradedRef.current) {
        setTimeout(() => {
          if (gradedRef.current) {
            const gradedAnnotation = annotate(gradedRef.current, {
              type: "box",
              color: "#28a745",
              iterations: 1,
              animationDuration: 800,
            });
            gradedAnnotation.show();
          }
        }, 1200);
      }

      // Annotate footer with bracket
      if (footerRef.current) {
        setTimeout(() => {
          if (footerRef.current) {
            const footerAnnotation = annotate(footerRef.current, {
              type: "bracket",
              color: "#6c757d",
              brackets: ["left", "right"],
              animationDuration: 600,
            });
            footerAnnotation.show();
          }
        }, 1400);
      }
    }
  }, [showStats, userStats]);

  const getScoreMessage = (percentage: number) => {
    if (percentage >= 90) {
      return "Wow! You're absolutely crushing it! 🌟✨";
    } else if (percentage >= 75) {
      return "Great work! You're doing amazing! Keep it up! 📈🎉";
    } else {
      return "Hey, every expert was once a beginner! Let's grow together! 💪🌱";
    }
  };

  const navBarStyle = {
    boxShadow: "0px 1px 20px 0px rgba(0, 0, 0, 1)",
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <nav
        className="sb-topnav navbar navbar-expand-lg navbar-dark bg-dark"
        style={navBarStyle}
      >
        <button
          className="btn btn-link btn-sm order-0"
          id="sidebarToggle"
          onClick={props.toggleSideBar}
        >
          <i className="fa fa-bars"></i>
        </button>

        <NavLink
          className="navbar-brand order-1 flex-grow-1"
          to="/"
          style={{ fontSize: "1rem" }}
        >
          {t.translate("Assessments Platform ")}
        </NavLink>

        {/* User Stats Icon */}
        {userStats && (
          <div className="position-relative order-2 me-3">
            <button
              className="btn btn-link p-0 position-relative"
              onClick={() => setShowStats(!showStats)}
              style={{ border: "none", background: "none" }}
            >
              <div className="position-relative d-inline-block">
                <img
                  src={
                    userStats.profile_pic || "https://via.placeholder.com/40"
                  }
                  alt="User"
                  className="rounded-circle border border-3 border-white shadow-sm"
                  style={{
                    width: "45px",
                    height: "45px",
                    objectFit: "cover",
                    transition: "transform 0.2s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "scale(1.05)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "scale(1)")
                  }
                />
                <div
                  className="position-absolute d-flex align-items-center justify-content-center bg-primary rounded-circle shadow"
                  style={{
                    bottom: "-4px",
                    right: "-4px",
                    width: "26px",
                    height: "26px",
                    border: "3px solid #212529",
                  }}
                >
                  <i
                    className="fa fa-cog text-white"
                    style={{
                      fontSize: "14px",
                    }}
                  ></i>
                </div>
              </div>
            </button>

            {showStats && (
              <div
                className="position-absolute bg-white shadow-lg rounded p-3"
                style={{
                  right: 0,
                  top: "50px",
                  minWidth: "320px",
                  maxWidth: "350px",
                  zIndex: 1050,
                  border: "1px solid #dee2e6",
                }}
              >
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h6 className="mb-0 fw-bold text-dark">📊 Your Progress!</h6>
                  {/* <button
                    className="btn-close"
                    onClick={() => setShowStats(false)}
                    aria-label="Close"
                  ></button> */}
                </div>

                <hr className="my-2" />

                {/* Average Score Section */}
                <div className="mb-3 p-3 bg-light rounded">
                  <div className="mb-2">
                    <small className="text-muted d-block mb-1">
                      🎯 Your average score is
                    </small>
                    <h3 className="mb-1 fw-bold text-center">
                      <span
                        ref={scoreRef}
                        style={{ padding: "4px 12px", display: "inline-block" }}
                      >
                        {userStats.average_percentage}%
                      </span>
                    </h3>
                  </div>
                  <p
                    className="text-center mb-0 small fw-semibold"
                    style={{ lineHeight: "1.6", color: "#495057" }}
                  >
                    {getScoreMessage(userStats.average_percentage)}
                  </p>
                </div>

                {/* Best Subject Section */}
                <div className="mb-3 p-3 bg-light rounded">
                  <small className="text-muted d-block mb-2">
                    🏆 Your top performance subject is
                  </small>
                  <div className="text-center">
                    <h6 className="mb-0 fw-bold text-primary">
                      <span
                        ref={subjectRef}
                        style={{ padding: "4px 12px", display: "inline-block" }}
                      >
                        {userStats.highest_performing_subject}
                      </span>
                    </h6>
                    <small className="text-muted d-block mt-1">
                      Keep shining in this area! ⭐
                    </small>
                  </div>
                </div>

                {/* Pending and Started Exams */}
                {(userStats.pending_exams_count > 0 ||
                  userStats.started_exams_count > 0) && (
                  <div className="mb-3 p-3 bg-light rounded">
                    <small className="text-muted d-block mb-2 text-center">
                      📚 Assessments in progress
                    </small>
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <div className="text-center p-2 bg-white rounded shadow-sm">
                          <div
                            ref={pendingRef}
                            className="fw-bold text-white d-inline-block"
                            style={{
                              fontSize: "1.8rem",
                              padding: "0 8px",
                              color: "#ffc107",
                            }}
                          >
                            {userStats.pending_exams_count}
                          </div>
                          <small className="text-muted d-block">
                            Pending ⏳
                          </small>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-center p-2 bg-white rounded shadow-sm">
                          <div
                            ref={startedRef}
                            className="fw-bold text-info d-inline-block text-light "
                            style={{ fontSize: "1.8rem", padding: "0 8px" }}
                          >
                            {userStats.started_exams_count}
                          </div>
                          <small className="text-muted d-block">
                            Started 🚀
                          </small>
                        </div>
                      </div>
                    </div>
                    <p
                      className="text-center mb-0 small fw-semibold"
                      style={{ lineHeight: "1.5", color: "#495057" }}
                    >
                      Complete these to boost your average even higher! 🎯💯
                    </p>
                  </div>
                )}

                {/* Graded Exams Count */}
                {/* <div className="text-center p-3 bg-success bg-opacity-10 rounded mb-2">
                <span ref={gradedRef} className="fw-bold text-success d-inline-block" style={{ fontSize: '1.2rem', padding: '2px 8px' }}>
                  {userStats.total_exams}
                </span>
                <small className="text-success ms-2 fw-semibold">Completed Assessments ✅</small>
              </div> */}

                {/* Footer Note */}
                <div className="text-center mt-2">
                  <small
                    className="text-muted"
                    style={{
                      fontSize: "0.75rem",
                      padding: "0 8px",
                      display: "inline-block",
                    }}
                  >
                    📊 Stats from your last 100 assessments
                  </small>
                </div>
              </div>
            )}
          </div>
        )}

        {/* <form className="d-none d-md-inline-block form-inline ml-auto mr-0 mr-md-3 my-2 my-md-0">
      <div className="input-group">
        <input className="form-control" type="text" placeholder="Search for..." aria-label="Search"
          aria-describedby="basic-addon2" />
        <div className="input-group-append">
          <button className="btn btn-primary" type="button"><i className="fa fa-search"></i></button>
        </div>
      </div>
    </form><Notifications /> */}
        <ul className="navbar-nav d-none">
          <li className="nav-item dropdown"></li>
        </ul>
      </nav>
    </>
  );
}

export default DashBoardNav;
